"use client";

import { FC } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import ResourceListingCard from "@/components/cards/ResourceListingCard";
import { IBannerSection } from "@/api/dtos/block-component.type";
import { resourcesInfiniteQuery } from "@/api/resource";
import { getImageUrl } from "@/utils/media.helper";

// ----------------------------------------------------------------------------
// Related case studies — auto-fetched from the same Strapi endpoint, with
// the current slug filtered out. Renders 3 cards at most so the grid stays
// tight. Reuses the listing-page `ResourceListingCard` for visual parity
// with /resources/case-study.
// ----------------------------------------------------------------------------

type ICaseStudyRelatedProps = {
  currentSlug: string;
};

const CaseStudyRelated: FC<ICaseStudyRelatedProps> = ({ currentSlug }) => {
  const { data, isLoading } = useInfiniteQuery({
    ...resourcesInfiniteQuery.all({
      slug: "/case-study-details",
      sort: "publishedAt:DESC",
      populate: "deep",
      pagination: { pageSize: 6 },
    }),
  });

  if (isLoading) return null;

  const allEntries = data?.pages.flatMap((p) => p.data) ?? [];
  const related = allEntries
    .filter((entry) => entry.attributes.slug !== currentSlug)
    .slice(0, 3);

  if (related.length === 0) return null;

  return (
    <section className="container py-12 lg:py-16 xl:py-20">
      <div className="mb-8 flex flex-col items-center gap-2 text-center md:mb-10">
        <span className="font-mono text-[11px] font-normal uppercase tracking-[0.15em] text-primary-900">
          Keep reading
        </span>
        <h2 className="font-bricolage-grotesque text-[clamp(1.75rem,2vw+1rem,2.5rem)] font-medium !leading-[1.15] text-black-900 [text-wrap:balance]">
          More case studies
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-7">
        {related.map((entry) => {
          const banner = entry.attributes.case_study_page?.find(
            (item) => item.__component === "blocks.banner-section",
          ) as IBannerSection | undefined;

          return (
            <ResourceListingCard
              key={entry.attributes.slug}
              kind="case-study"
              heading={banner?.heading ?? entry.attributes.page_name}
              image={{
                src: getImageUrl(entry.attributes.card_image),
                alt: entry.attributes.card_image?.name ?? "",
              }}
              link={`/resources/case-study/${entry.attributes.slug}`}
            />
          );
        })}
      </div>
    </section>
  );
};

export default CaseStudyRelated;
